import {
  Actor,
  Engine,
  vec,
  Vector,
  PointerEvent,
  // GpuParticleEmitter,
  CollisionType,
} from 'excalibur';
import { EventsManager, Unsubscribe } from '@game-libs/events-manager';
import { GameplayEvents } from '../types/game-events';
import { ShockWavePostProcessor } from '@game-libs/exc-libs/src/shaders';
import { HitEffect } from './hitEffect';
// import { showerParticle } from '@game-libs/exc-libs';
import { dragonAnimation } from './animations';
import { BehaviorState, DragonBehaviour } from './dragonBehaviour';
import { DeathExplosion } from './deathExplosion';
import { Images } from './resources';
import { TutorialLevel } from './tutorialLevel';
import { DamageSource } from '../types/game-types';

export class LittleDragon extends Actor {
  subscriptions: Unsubscribe[] = [];
  dragonBehaviour = new DragonBehaviour();
  dragon = new Actor({
    collisionType: CollisionType.PreventCollision,
  });

  private engine?: Engine;
  private shockwave?: ShockWavePostProcessor;
  // Pending roar shockwave timers — cleared on cleanup so removed dragons don't keep firing.
  private roarTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor() {
    super({
      collisionType: CollisionType.PreventCollision,
    });

    this.scale = vec(2.5, 2.5);

    this.z = this.pos.y;
  }

  onAdd(engine: Engine): void {
    const { on, emit } = EventsManager();

    this.subscriptions.push(
      on(GameplayEvents.DAMAGE_DRAGON, ({ isCritical: isCriticalHit, source }) => {
        if (source === DamageSource.Click) {
          const hitPosition = (this.scene as TutorialLevel).getDragonPosition(vec(0, -80));
          const hitEffect = new HitEffect(hitPosition, isCriticalHit);
          this.scene?.add(hitEffect);
        }
      }),
    );

    this.dragon.on('pointerdown', (evt: PointerEvent) => {
      emit(GameplayEvents.CLICK_GAME_DRAGON);
    });

    // this.dragon.on('pointerenter', (evt: PointerEvent) => {
    //   emit(GameplayEvents.CLICK_GAME_DRAGON);
    // });

    this.dragon.on('pointerup', (evt: PointerEvent) => {
      emit(GameplayEvents.STOP_CLICK_GAME_DRAGON);
    });

    this.dragon.on('pointerleave', (evt: PointerEvent) => {
      emit(GameplayEvents.STOP_CLICK_GAME_DRAGON);
    });

    this.engine = engine;
    this.shockwave = new ShockWavePostProcessor();
    this.shockwave.init(engine.currentScene);
    engine.graphicsContext.addPostProcessor(this.shockwave);

    const shadow = new Actor({
      width: 100,
      height: 100,
      collisionType: CollisionType.PreventCollision,
    });
    shadow.scale = vec(0.45, 0.45);
    shadow.graphics.use(Images.UnderShadow.toSprite());
    this.addChild(shadow);
    shadow.pos.y -= 6;
    shadow.z = this.pos.y - 10;

    this.dragon.scale = vec(1, 1);
    this.dragon.graphics.use(dragonAnimation.idleCenter);

    this.dragon.anchor.setTo(0.5, 1);
    this.addChild(this.dragon);
    this.dragon.z = this.pos.y;

    this.dragonBehaviour.transitionClock.start();
    this.dragonBehaviour.setActions({
      [BehaviorState.Center]: () => {
        this.dragon.graphics.use(dragonAnimation.idleCenter);
      },
      [BehaviorState.Left]: () => {
        this.dragon.graphics.use(dragonAnimation.idleLeft);
      },
      [BehaviorState.Right]: () => {
        this.dragon.graphics.use(dragonAnimation.idleRight);
      },
      [BehaviorState.Roar]: () => {
        this.dragon.graphics.use(dragonAnimation.roar);
      },
    });

    this.subscriptions.push(
      on(GameplayEvents.DRAGON_ROAR, () => {
        this.dragonBehaviour.transitionToForcedState(BehaviorState.Roar);
        this.roar(vec(this.pos.x / engine.canvasWidth, (this.pos.y - 100) / engine.canvasHeight));
      }),
    );
  }

  public startDeathSequence() {
    this.dragonBehaviour.transitionClock.stop();
    this.dragon.graphics.use(dragonAnimation.dying);
    this.actions.fade(0, 3000).die();

    const deathExplosion = new DeathExplosion(this.pos.add(vec(0, -70)));
    this.scene?.add(deathExplosion);
  }

  roar(position: Vector) {
    this.clearRoarTimeouts();

    const shockwave = this.shockwave;
    if (!shockwave) {
      return;
    }

    this.roarTimeouts.push(
      setTimeout(() => {
        shockwave.triggerShockWave(position, 800, 10, 0.6, 0.08);
      }, 100),
      setTimeout(() => {
        shockwave.triggerShockWave(position, 800, 11, 0.8, 0.06);
      }, 400),
      setTimeout(() => {
        shockwave.triggerShockWave(position, 800, 12, 0.9, 0.04);
      }, 600),
      setTimeout(() => {
        shockwave.triggerShockWave(position, 800, 13, 1, 0.02);
      }, 800),
    );
  }

  private clearRoarTimeouts() {
    for (const timeoutId of this.roarTimeouts) {
      clearTimeout(timeoutId);
    }
    this.roarTimeouts = [];
  }

  onRemove() {
    this.cleanup();
  }

  cleanup() {
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
    this.subscriptions = [];

    this.clearRoarTimeouts();

    if (this.shockwave && this.engine) {
      this.shockwave.dispose();
      this.engine.graphicsContext.removePostProcessor(this.shockwave);
    }
    this.shockwave = undefined;
    this.engine = undefined;
  }
}
